import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { useAuth } from '../lib/AuthContext';
import { uploadDataset, downloadDataset, saveReport, getUsage, callGenerateReport } from '../lib/supabase';
import {
  parseLog, buildAnalyticsSummary,
  computeCycleTime, computePCE, computeVariants, computeHandoffs,
  computeStepDurations, computeResourceUtil, buildRateCard, computeABC, arrStats, computeProcessMap, computeResourceActivityMatrix
} from '../lib/analytics';

const PLAN_LIMITS = { free:{ uploads:3, reports:5 }, pro:{ uploads:Infinity, reports:Infinity } };
const TABS = [
  {id:'cycle',   label:'Cycle Time',    icon:'⏱', group:'Process'},
  {id:'pce',     label:'PCE',           icon:'⚡', group:'Process'},
  {id:'variants',label:'Variants',      icon:'🌿', group:'Process'},
  {id:'handoffs',label:'Handoffs',      icon:'🤝', group:'Process'},
  {id:'steps',   label:'Step Durations',icon:'📊', group:'Process'},
  {id:'procmap', label:'Process Map',   icon:'🗺', group:'Process'},
  {id:'cost',    label:'Rate Card',     icon:'💰', group:'Cost'},
  {id:'abc',     label:'ABC Analysis',  icon:'🧮', group:'Cost'},
  {id:'resutil',  label:'Resource Util',      icon:'👥', group:'Resources'},
  {id:'resmatrix', label:'Activity Matrix',    icon:'🔢', group:'Resources'},
];

export default function AnalysePage() {
  const { session, profile } = useAuth();
  const location  = useLocation();
  const navigate  = useNavigate();
  const inputRef  = useRef();
  const userId    = session?.user?.id;
  const plan      = profile?.plan || 'free';
  const limits    = PLAN_LIMITS[plan];

  // Data state
  const [events,    setEvents]    = useState(null);
  const [file,      setFile]      = useState(null);
  const [datasetId, setDatasetId] = useState(location.state?.datasetId || null);
  const [dragOver,  setDragOver]  = useState(false);
  const [rateCard,  setRateCard]  = useState([]);
  const [schedule,  setSchedule]  = useState({ hoursPerDay: 7.5, daysPerWeek: 5 });

  // UI state
  const [active,    setActive]    = useState(null);
  const [result,    setResult]    = useState(null);
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);
  const [saving,    setSaving]    = useState('');
  const [genStatus, setGenStatus] = useState('');
  const [usage,     setUsage]     = useState({ uploads:0, reports:0 });

  const nCases = events ? new Set(events.map(e=>e.caseId)).size : 0;
  const nRes   = events ? new Set(events.map(e=>e.res)).size : 0;

  useEffect(()=>{ if(userId) getUsage(userId).then(setUsage).catch(()=>{}); },[userId]);

  // Load existing dataset if coming from dashboard
  useEffect(()=>{
    const state = location.state;
    if (state?.filePath && !events) {
      setLoading(true);
      setSaving('Loading dataset…');
      downloadDataset(state.filePath).then(blob=>{
        const reader = new FileReader();
        reader.onload = (ev)=>{
          const wb = XLSX.read(ev.target.result,{type:'array',cellDates:true});
          const ws = wb.Sheets['Process Data']||wb.Sheets[wb.SheetNames[0]];
          const rows = XLSX.utils.sheet_to_json(ws,{header:1,defval:''});
          const evs  = parseLog(rows);
          setEvents(evs);
          setFile({name:state.name});
          setSaving('');
          setLoading(false);
        };
        reader.readAsArrayBuffer(blob);
      }).catch(e=>{ setSaving(''); setError(e.message); setLoading(false); });
    }
  },[]);

  const processFile = useCallback(async(f)=>{
    setLoading(true); setError(null); setResult(null); setActive(null);
    try {
      const buf  = await f.arrayBuffer();
      const wb   = XLSX.read(buf,{type:'array',cellDates:true});
      const ws   = wb.Sheets['Process Data']||wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws,{header:1,defval:''});
      const evs  = parseLog(rows);
      if(!evs.length) throw new Error('No valid events. Check row 1 has headers and Col F has Case IDs.');
      setEvents(evs); setFile(f); setRateCard([]);

      // Auto-save to Supabase
      if (userId && usage.uploads < limits.uploads) {
        setSaving('Saving dataset…');
        try {
          const nC = new Set(evs.map(e=>e.caseId)).size;
          const ds = await uploadDataset(userId, f, evs.length, nC);
          setDatasetId(ds.id);
          const newUsage = await getUsage(userId);
          setUsage(newUsage);
        } catch(se) { console.warn('Save failed:', se.message); }
        setSaving('');
      }
    } catch(e) { setError(e.message); }
    setLoading(false);
  },[userId, usage, limits]);

  const run = useCallback((id)=>{
    if(!events) return;
    setActive(id); setLoading(true); setResult(null); setError(null);
    setTimeout(()=>{
      try {
        const rc = rateCard.length ? rateCard : buildRateCard(events);
        let d;
        switch(id) {
          case 'cycle':    d=computeCycleTime(events); break;
          case 'pce':      d=computePCE(events); break;
          case 'variants': d=computeVariants(events); break;
          case 'handoffs': d=computeHandoffs(events); break;
          case 'steps':    d=computeStepDurations(events); break;
          case 'cost':     { const c=buildRateCard(events); setRateCard(c); d=c; break; }
          case 'abc':      d=computeABC(events,rc); break;
          case 'resutil':  d=computeResourceUtil(events, schedule); break;
          case 'resmatrix': d=computeResourceActivityMatrix(events); break;
          case 'procmap':  d=computeProcessMap(events); break;
          default: break;
        }
        setResult({id,data:d});
      } catch(e){ setError(e.message); }
      setLoading(false);
    },30);
  },[events, rateCard]);

  const exportExcel = useCallback(()=>{
    if(!result) return;
    const wb = XLSX.utils.book_new();
    const add=(name,rows)=>XLSX.utils.book_append_sheet(wb,XLSX.utils.json_to_sheet(rows),name.slice(0,31));
    const {id,data}=result;
    if(id==='cycle')    { add('Cycle Time',data.rows); add('Stats',[data.stats]); }
    if(id==='pce')      { add('PCE',data.rows); add('Stats',[data.stats]); }
    if(id==='variants') { add('Variants',data.rows); }
    if(id==='handoffs') { add('Handoffs',data.rows); add('Pairs',data.pairRows); }
    if(id==='steps')    { data.steps.forEach(s=>add(s.step.slice(0,31),s.obs)); add('Stats',data.steps.map(s=>({Step:s.step,...s.stats}))); }
    if(id==='cost')     { add('Rate Card',data); }
    if(id==='abc')      { add('ABC',data.rows); }
    if(id==='resutil')  { add('Utilisation',data.rows); }
    XLSX.writeFile(wb,`analytics_${id}_${Date.now()}.xlsx`);
  },[result]);

  async function generateReport(type) {
    if(!events) return;
    if(usage.reports >= limits.reports) {
      setGenStatus('Report limit reached. Upgrade to Pro for unlimited reports.');
      return;
    }
    setGenStatus(`Generating ${type==='word'?'Word report':'PowerPoint'}…`);
    try {
      const summary = buildAnalyticsSummary(events, rateCard);
      const fname = `${file?.name?.replace(/\.[^.]+$/,'') || 'report'}_${type==='word'?'report':'presentation'}_${Date.now()}.${type==='word'?'doc':'pptx'}`;
      const edgeUrl = 'https://rscpzkjfmpfrmpjgmxtf.supabase.co/functions/v1/generate-report';
      const key = process.env.REACT_APP_SUPABASE_ANON_KEY || '';

      if (type === 'word') {
        const response = await fetch(edgeUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': key,
            'Authorization': `Bearer ${key}`,
          },
          body: JSON.stringify({ analyticsSummary: summary, reportType: 'word' })
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.error || `HTTP ${response.status}`);
        }
        const blob = await response.blob();
        const dlUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = dlUrl; a.download = fname; a.click();
        URL.revokeObjectURL(dlUrl);
        if(userId && datasetId) {
          try {
            await saveReport(userId, datasetId, type, fname, blob);
            const newUsage = await getUsage(userId);
            setUsage(newUsage);
          } catch(se){ console.warn('Report save failed:', se.message); }
        }
      } else {
        const response = await fetch(edgeUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': key,
            'Authorization': `Bearer ${key}`,
          },
          body: JSON.stringify({ analyticsSummary: summary, reportType: 'pptx' })
        });
        if (!response.ok) {
          const err = await response.json().catch(() => ({}));
          throw new Error(err.error || `HTTP ${response.status}`);
        }
        const content = await response.json();
        const blob = await buildPPTXDoc(content, summary);
        const dlUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = dlUrl; a.download = fname; a.click();
        URL.revokeObjectURL(dlUrl);
        if(userId && datasetId) {
          try {
            await saveReport(userId, datasetId, type, fname, blob);
            const newUsage = await getUsage(userId);
            setUsage(newUsage);
          } catch(se){ console.warn('Report save failed:', se.message); }
        }
      }
      setGenStatus('');
    } catch(e) {
      setGenStatus('Error: '+e.message);
    }
  }

  const groups = [...new Set(TABS.map(t=>t.group))];

  return (
    <div style={S.page}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* Top bar */}
      <div style={S.topbar}>
        <button style={S.backBtn} onClick={()=>navigate('/dashboard')}>← Dashboard</button>
        <div style={S.fileInfo}>
          {file ? <>
            <span style={{color:'#3b82f6',fontWeight:600}}>{file.name}</span>
            <span style={{color:'#1e293b'}}>|</span>
            <span style={{color:'#475569'}}>{events?.length?.toLocaleString()} events · {nCases} cases · {nRes} resources</span>
          </> : <span style={{color:'#334155'}}>No file loaded</span>}
          {saving && <span style={{color:'#fde68a',fontSize:11,fontFamily:"'IBM Plex Mono',monospace"}}>{saving}</span>}
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          {events && <>
            <GenBtn label="📄 Word Report"   loading={genStatus.includes('Word')}  disabled={!!genStatus||usage.reports>=limits.reports} onClick={()=>generateReport('word')}  color="#7c3aed"/>
            <GenBtn label="📊 PowerPoint"    loading={genStatus.includes('PowerPoint')} disabled={!!genStatus||usage.reports>=limits.reports} onClick={()=>generateReport('pptx')}  color="#1d4ed8"/>
          </>}
          {result && <button style={S.xlsBtn} onClick={exportExcel}>⬇ Excel</button>}
        </div>
      </div>

      {genStatus && <div style={S.statusBar}>{genStatus}</div>}

      <div style={{display:'flex',flex:1,overflow:'hidden'}}>
        {/* Sidebar */}
        <nav style={S.sidebar}>
          {groups.map(grp=>(
            <div key={grp} style={{marginBottom:6}}>
              <div style={S.grpLabel}>{grp}</div>
              {TABS.filter(t=>t.group===grp).map(t=>{
                const isA=active===t.id;
                return <button key={t.id} style={{...S.tabBtn, background:isA?'#0d2244':'transparent', color:isA?'#93c5fd':events?'#475569':'#0f1a2e', border:isA?'1px solid #1e3a5f':'1px solid transparent'}} disabled={!events} onClick={()=>run(t.id)}>
                  <span style={{fontSize:13}}>{t.icon}</span>{t.label}
                </button>;
              })}
            </div>
          ))}
        </nav>

        {/* Content */}
        <div style={S.content}>
          {!events ? (
            <DropZone dragOver={dragOver} setDragOver={setDragOver} inputRef={inputRef} onFile={processFile}/>
          ) : loading ? (
            <div style={S.center}><div style={{width:28,height:28,border:'3px solid #1e2d4a',borderTop:'3px solid #3b82f6',borderRadius:'50%',animation:'spin 0.8s linear infinite'}}/></div>
          ) : error ? (
            <div style={S.errorBox}>⚠ {error}</div>
          ) : result ? (
            <ResultPanel result={result} rateCard={rateCard} setRateCard={setRateCard} schedule={schedule} setSchedule={setSchedule} run={run}/>
          ) : (
            <div style={{...S.center,flexDirection:'column',gap:10}}>
              <div style={{fontSize:28,opacity:.3}}>←</div>
              <div style={{color:'#1e3a5f',fontSize:12,fontFamily:"'IBM Plex Mono',monospace"}}>SELECT AN ANALYSIS FROM THE SIDEBAR</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function GenBtn({label, loading, disabled, onClick, color}) {
  return <button style={{background:color+'22',color:loading?'#6b7280':color.replace('3aed','c084fc').replace('4ed8','60a5fa'), border:`1px solid ${color}44`,padding:'7px 14px',borderRadius:7,fontSize:11.5,fontWeight:600,cursor:disabled?'not-allowed':'pointer',opacity:disabled?0.5:1,fontFamily:"'IBM Plex Mono',monospace"}} disabled={disabled} onClick={onClick}>{loading?'⏳ GENERATING…':label}</button>;
}

function DropZone({dragOver,setDragOver,inputRef,onFile}) {
  const [loadingSample, setLoadingSample] = React.useState('');

  const samples = [
    { name:'Hospital Patient Flow',  file:'hospital_process_data.xlsx',     icon:'🏥', desc:'80 patients · 3,794 events · Emergency & Elective' },
    { name:'Loan Processing',        file:'loan_processing_analytics.xlsx',  icon:'💳', desc:'50 loans · 713 events · Personal, Mortgage, Auto, Business' },
    { name:'Fashion Retail Store',   file:'retail_store_analytics.xlsx',     icon:'👗', desc:'60 transactions · 1,368 events · Purchase, Return, Styling' },
  ];

  async function loadSample(s) {
    setLoadingSample(s.file);
    try {
      const response = await fetch(`/samples/${s.file}`);
      if (!response.ok) throw new Error('File not found. Make sure sample files are in public/samples/');
      const blob = await response.blob();
      const file = new File([blob], s.file, { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      onFile(file);
    } catch(e) {
      alert('Could not load sample: ' + e.message);
    }
    setLoadingSample('');
  }

  return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100%',overflowY:'auto'}}>
      <div style={{maxWidth:500,width:'100%',padding:'0 16px'}}>
        <div onDrop={e=>{e.preventDefault();setDragOver(false);const f=e.dataTransfer.files[0];if(f)onFile(f);}} onDragOver={e=>{e.preventDefault();setDragOver(true);}} onDragLeave={()=>setDragOver(false)} onClick={()=>inputRef.current?.click()} style={{border:`1px dashed ${dragOver?'#3b82f6':'#1e293b'}`,borderRadius:12,padding:'36px 48px',textAlign:'center',cursor:'pointer',background:dragOver?'#07142a':'transparent',transition:'all .2s',marginBottom:20}}>
          <div style={{fontSize:36,marginBottom:10}}>📂</div>
          <div style={{fontSize:16,fontWeight:600,marginBottom:4,color:'#cbd5e1'}}>Drop your Process Data file</div>
          <div style={{fontSize:11,color:'#334155',marginBottom:14}}>.xlsx · .xlsm · .csv</div>
          <div style={{background:'#0d2244',color:'#3b82f6',border:'1px solid #1e3a5f',padding:'7px 20px',borderRadius:6,fontSize:11.5,fontWeight:600,display:'inline-block',fontFamily:"'IBM Plex Mono',monospace"}}>BROWSE FILES</div>
          <div style={{marginTop:14,fontSize:8.5,color:'#1e293b',fontFamily:"'IBM Plex Mono',monospace",lineHeight:1.8,borderTop:'1px solid #0f1a2e',paddingTop:10}}>Row 1 = headers · A=ActID · B=Resource · C=Step · D=Timestamp · E=EventType · F=CaseID</div>
        </div>
        <div>
          <div style={{fontSize:9,fontWeight:700,letterSpacing:'0.14em',textTransform:'uppercase',color:'#334155',fontFamily:"'IBM Plex Mono',monospace",marginBottom:10,textAlign:'center'}}>— OR LOAD A SAMPLE DATASET —</div>
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {samples.map(s=>(
              <button key={s.file} onClick={()=>loadSample(s)} disabled={!!loadingSample}
                style={{display:'flex',alignItems:'center',gap:12,background:'#0a1525',border:'1px solid #1e2d4a',borderRadius:8,padding:'11px 14px',cursor:loadingSample?'wait':'pointer',textAlign:'left',fontFamily:"'DM Sans',system-ui,sans-serif",width:'100%',opacity:loadingSample&&loadingSample!==s.file?0.5:1,transition:'border-color .15s'}}
                onMouseOver={e=>{if(!loadingSample)e.currentTarget.style.borderColor='#3b82f6'}}
                onMouseOut={e=>e.currentTarget.style.borderColor='#1e2d4a'}>
                <span style={{fontSize:22,flexShrink:0}}>{s.icon}</span>
                <div style={{flex:1}}>
                  <div style={{fontSize:12.5,fontWeight:600,color:'#cbd5e1',marginBottom:2}}>{s.name}</div>
                  <div style={{fontSize:10,color:'#475569',fontFamily:"'IBM Plex Mono',monospace"}}>{s.desc}</div>
                </div>
                <span style={{fontSize:10,color:loadingSample===s.file?'#3b82f6':'#334155',fontFamily:"'IBM Plex Mono',monospace",flexShrink:0}}>
                  {loadingSample===s.file?'Loading...':'Load ->'}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
      <input ref={inputRef} type="file" accept=".xlsx,.xlsm,.csv" style={{display:'none'}} onChange={e=>e.target.files[0]&&onFile(e.target.files[0])}/>
    </div>
  );
}

// ── Result Panel (simplified tables & stats) ─────────────────────────────────
function ResultPanel({ result, rateCard, setRateCard, schedule, setSchedule, run }) {
  const {id, data} = result;
  const fmt = n => typeof n==='number' ? (n>=1000?n.toLocaleString('en-US',{maximumFractionDigits:0}):n>=10?n.toFixed(1):n.toFixed(2)) : String(n??'—');

  function StatCard({label,value,unit,color='#3b82f6'}) {
    return <div style={{background:'#0b1628',border:`1px solid ${color}25`,borderTop:`2px solid ${color}`,borderRadius:6,padding:'8px 13px',minWidth:84}}>
      <div style={{fontSize:8.5,color:'#475569',fontFamily:"'IBM Plex Mono',monospace",letterSpacing:'0.1em',textTransform:'uppercase',marginBottom:3}}>{label}</div>
      <div style={{fontSize:18,fontWeight:700,color,fontFamily:"'IBM Plex Mono',monospace",lineHeight:1}}>{typeof value==='number'?fmt(value):value}</div>
      {unit&&<div style={{fontSize:8,color:'#334155',marginTop:2,fontFamily:"'IBM Plex Mono',monospace"}}>{unit}</div>}
    </div>;
  }

  function DT({cols,rows}) {
    return <div style={{overflowX:'auto',overflowY:'auto',maxHeight:340,borderRadius:6,border:'1px solid #1e293b'}}>
      <table style={{borderCollapse:'collapse',width:'100%',fontSize:11}}>
        <thead><tr>{cols.map(c=><th key={c.k} style={{padding:'6px 10px',background:'#0d1e35',color:'#64748b',textAlign:c.r?'right':'left',position:'sticky',top:0,fontSize:9.5,fontWeight:600,letterSpacing:'0.08em',textTransform:'uppercase',whiteSpace:'nowrap',borderBottom:'1px solid #1e293b'}}>{c.l}</th>)}</tr></thead>
        <tbody>{rows.slice(0,400).map((row,i)=><tr key={i} style={{background:i%2===0?'#070f1c':'#0a1525'}}>
          {cols.map(c=>{const v=row[c.k],num=typeof v==='number',pceC=c.pce&&num?(v>=60?'#10b981':v>=30?'#f59e0b':'#ef4444'):null;
          return <td key={c.k} style={{padding:'5px 10px',borderBottom:'1px solid #0f1a2e',textAlign:c.r||num?'right':'left',color:pceC||(num?'#94a3b8':'#cbd5e1'),fontFamily:num?"'IBM Plex Mono',monospace":undefined,whiteSpace:c.wrap?'normal':'nowrap',maxWidth:c.wrap?380:undefined}}>{v===null||v===undefined?'—':num?(c.dec!==undefined?v.toFixed(c.dec):v%1?v.toFixed(2):v):String(v)}</td>;})}</tr>)}
        </tbody>
      </table>
    </div>;
  }

  if(id==='cycle') { const s=data.stats; return <div><div style={SC.row}>{[[s.n,'N Cases','cases','#3b82f6'],[s.mean,'Mean','min','#f59e0b'],[s.median,'Median','min','#10b981'],[s.p95,'P95','min','#ef4444'],[s.stddev,'StdDev','min','#8b5cf6']].map(([v,l,u,c])=><StatCard key={l} label={l} value={v} unit={u} color={c}/>)}</div><DT cols={[{k:'caseId',l:'Case ID'},{k:'cycleMins',l:'Cycle (min)',r:true,dec:1}]} rows={data.rows}/></div>; }
  if(id==='pce')   { const s=data.stats; return <div><div style={SC.row}>{[[s.mean,'Mean PCE','%','#10b981'],[s.median,'Median','%','#3b82f6'],[s.p95,'P95','%','#f59e0b'],[s.min,'Min','%','#475569'],[s.max,'Max','%','#ef4444']].map(([v,l,u,c])=><StatCard key={l} label={l} value={v} unit={u} color={c}/>)}</div><DT cols={[{k:'caseId',l:'Case ID'},{k:'cycleMins',l:'Cycle (min)',r:true,dec:1},{k:'va',l:'VA (min)',r:true,dec:1},{k:'bva',l:'BVA',r:true,dec:1},{k:'nva',l:'NVA',r:true,dec:1},{k:'pce',l:'PCE %',r:true,dec:1,pce:true}]} rows={data.rows}/></div>; }
  if(id==='variants') return <div><div style={SC.row}><StatCard label="Total Cases" value={data.totalCases} color="#3b82f6"/><StatCard label="Variants" value={data.rows.length} color="#8b5cf6"/><StatCard label="Top %" value={+data.rows[0]?.pct||0} unit="of cases" color="#f59e0b"/></div><DT cols={[{k:'count',l:'Count',r:true,dec:0},{k:'pct',l:'%',r:true,dec:1},{k:'avgCycle',l:'Avg Cycle',r:true,dec:1},{k:'pce',l:'PCE%',r:true,dec:1,pce:true},{k:'variant',l:'Sequence',wrap:true}]} rows={data.rows}/></div>;
  if(id==='handoffs') { const s=data.stats; return <div><div style={SC.row}>{[[data.total,'Total','handoffs','#f59e0b'],[s.mean,'Mean/Case','','#3b82f6'],[s.max,'Max','','#ef4444']].map(([v,l,u,c])=><StatCard key={l} label={l} value={v} unit={u} color={c}/>)}</div><DT cols={[{k:'caseId',l:'Case ID'},{k:'handoffs',l:'Handoffs',r:true,dec:0},{k:'sequence',l:'Sequence',wrap:true}]} rows={data.rows}/></div>; }
  if(id==='steps') return <div>
    <div style={SC.slabel}>Step Statistics</div>
    <DT cols={[{k:'step',l:'Step'},{k:'n',l:'N',r:true,dec:0},{k:'mean',l:'Mean',r:true,dec:1},{k:'median',l:'Median',r:true,dec:1},{k:'p95',l:'P95',r:true,dec:1},{k:'stddev',l:'StdDev',r:true,dec:1}]} rows={data.steps.map(s=>({step:s.step,n:s.stats.n,mean:s.stats.mean,median:s.stats.median,p95:s.stats.p95,stddev:s.stats.stddev}))}/>
  </div>;
  if(id==='cost') return <div>
    <div style={{...SC.slabel,marginBottom:8}}>Edit hourly rates below, then run ABC Analysis</div>
    <div style={{overflowX:'auto',overflowY:'auto',maxHeight:420,borderRadius:6,border:'1px solid #1e293b'}}>
      <table style={{borderCollapse:'collapse',width:'100%',fontSize:11}}>
        <thead><tr>{['Process Step','Resource','Type','Cost / Hour ($)'].map(h=><th key={h} style={{padding:'6px 10px',background:'#0d1e35',color:'#64748b',textAlign:'left',position:'sticky',top:0,fontSize:9.5,fontWeight:600,letterSpacing:'0.08em'}}>{h}</th>)}</tr></thead>
        <tbody>{rateCard.map((row,i)=><tr key={i} style={{background:i%2===0?'#070f1c':'#0a1525'}}>
          <td style={{padding:'4px 10px',color:'#cbd5e1'}}>{row.step}</td>
          <td style={{padding:'4px 10px',color:'#94a3b8'}}>{row.res}</td>
          <td style={{padding:'4px 10px',color:row.type==='Human'?'#86efac':'#64748b',fontSize:10}}>{row.type}</td>
          <td style={{padding:'3px 6px'}}><input type="number" min="0" step="5" value={row.costPerHour} onChange={e=>{const nr=[...rateCard];nr[i]={...nr[i],costPerHour:+e.target.value};setRateCard(nr);}} style={{width:80,background:'#0b1628',border:'1px solid #1e3a5f',color:'#fde68a',padding:'3px 8px',borderRadius:4,fontSize:11,fontFamily:"'IBM Plex Mono',monospace"}}/></td>
        </tr>)}</tbody>
      </table>
    </div>
  </div>;
  if(id==='abc') { const s=data.stats; return <div><div style={SC.row}><StatCard label="Grand Total" value={'$'+(+data.grandTotal.toFixed(0)).toLocaleString()} color="#f59e0b"/><StatCard label="Mean/Case" value={s.mean} unit="$" color="#3b82f6"/><StatCard label="P95" value={s.p95} unit="$" color="#ef4444"/></div><DT cols={[{k:'caseId',l:'Case ID'},{k:'total',l:'Total ($)',r:true,dec:2},{k:'vaCost',l:'VA ($)',r:true,dec:2},{k:'nvaCost',l:'NVA ($)',r:true,dec:2},{k:'vaPct',l:'VA% Cost',r:true,dec:1,pce:true},{k:'topAct',l:'Highest Cost Activity',wrap:true}]} rows={data.rows}/></div>; }
  if(id==='resutil') return (
    <div>
      {/* Schedule Configuration */}
      <div style={{background:'#0a1525',border:'1px solid #1e293b',borderRadius:8,padding:'14px 16px',marginBottom:16,display:'flex',alignItems:'center',gap:24,flexWrap:'wrap'}}>
        <div style={{fontSize:9,fontWeight:700,letterSpacing:'0.14em',textTransform:'uppercase',color:'#334155',fontFamily:"'IBM Plex Mono',monospace",flexShrink:0}}>SCHEDULE CONFIG</div>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <span style={{fontSize:10,color:'#64748b',fontFamily:"'IBM Plex Mono',monospace"}}>Hours/day</span>
          <input type="number" min="1" max="24" step="0.5"
            value={schedule.hoursPerDay}
            onChange={e=>{ const v=Math.min(24,Math.max(0.5,parseFloat(e.target.value)||7.5)); setSchedule(s=>({...s,hoursPerDay:v})); setTimeout(()=>run('resutil'),100); }}
            style={{width:64,background:'#0d2244',border:'1px solid #1e3a5f',borderRadius:4,color:'#93c5fd',fontSize:11,padding:'4px 8px',fontFamily:"'IBM Plex Mono',monospace",textAlign:'center'}}/>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <span style={{fontSize:10,color:'#64748b',fontFamily:"'IBM Plex Mono',monospace"}}>Days/week</span>
          <div style={{display:'flex',gap:4}}>
            {[3,4,5,6,7].map(d=>(
              <button key={d} onClick={()=>{ setSchedule(s=>({...s,daysPerWeek:d})); setTimeout(()=>run('resutil'),100); }}
                style={{width:28,height:28,borderRadius:4,border:'1px solid',cursor:'pointer',fontSize:11,fontWeight:600,fontFamily:"'IBM Plex Mono',monospace",
                  background:schedule.daysPerWeek===d?'#1e3a5f':'transparent',
                  color:schedule.daysPerWeek===d?'#93c5fd':'#334155',
                  borderColor:schedule.daysPerWeek===d?'#3b82f6':'#1e293b'}}>
                {d}
              </button>
            ))}
          </div>
        </div>
        {data && <div style={{marginLeft:'auto',fontSize:9,color:'#334155',fontFamily:"'IBM Plex Mono',monospace"}}>
          {data.workingDays} working days · {data.schedHrsPerResource}h scheduled per resource
        </div>}
      </div>

      {/* Stats row */}
      {data && <div style={{display:'flex',gap:8,marginBottom:14}}>
        {[
          [data.workingDays,'Working Days','in period','#3b82f6'],
          [data.hoursPerDay+'h','Hours / Day','per resource','#8b5cf6'],
          [data.daysPerWeek,'Days / Week','configured','#10b981'],
          [data.schedHrsPerResource+'h','Sched Hours','per resource','#f59e0b'],
        ].map(([v,l,u,c])=>(
          <div key={l} style={{background:'#0b1628',border:`1px solid ${c}25`,borderTop:`2px solid ${c}`,borderRadius:6,padding:'7px 12px',minWidth:80}}>
            <div style={{fontSize:8,color:'#475569',fontFamily:"'IBM Plex Mono',monospace",textTransform:'uppercase',letterSpacing:'0.1em',marginBottom:2}}>{l}</div>
            <div style={{fontSize:18,fontWeight:700,color:c,fontFamily:"'IBM Plex Mono',monospace"}}>{v}</div>
            <div style={{fontSize:7.5,color:'#334155',fontFamily:"'IBM Plex Mono',monospace"}}>{u}</div>
          </div>
        ))}
      </div>}

      <DT cols={[
        {k:'res',l:'Resource'},
        {k:'cases',l:'Cases',r:true,dec:0},
        {k:'busyHrs',l:'Busy (hrs)',r:true,dec:2},
        {k:'schedHrs',l:'Sched (hrs)',r:true,dec:1},
        {k:'idleHrs',l:'Idle (hrs)',r:true,dec:2},
        {k:'util',l:'Util %',r:true,dec:1,pce:true},
        {k:'vaPct',l:'VA% Busy',r:true,dec:1,pce:true},
      ]} rows={data.rows}/>
    </div>
  );
  if(id==='resmatrix') return <ResourceActivityMatrix data={data}/>;
  if(id==='procmap') return <ProcessMapPanel data={data}/>;
  return null;
}


// ── Resource Activity Matrix ───────────────────────────────────────────────────
function ResourceActivityMatrix({ data }) {
  const { rows, steps, colTotals } = data;
  const [view, setView] = React.useState('hours');   // 'hours' | 'count' | 'heatmap'
  const [sortBy, setSortBy] = React.useState('total'); // 'total' | 'name' | step name
  const [minHrs, setMinHrs] = React.useState(0);

  const viewOptions = [
    { v:'hours',   l:'Hours' },
    { v:'count',   l:'Count' },
    { v:'heatmap', l:'Heat Map' },
  ];

  // Sort rows
  const sorted = [...rows].filter(r => r.totalHrs >= minHrs).sort((a, b) => {
    if (sortBy === 'total') return b.totalMins - a.totalMins;
    if (sortBy === 'name')  return a.res.localeCompare(b.res);
    const aCell = a.cells.find(c => c.step === sortBy);
    const bCell = b.cells.find(c => c.step === sortBy);
    return (bCell?.mins || 0) - (aCell?.mins || 0);
  });

  // Max value for heat map scaling
  const allValues = rows.flatMap(r => r.cells.map(c => view === 'count' ? c.count : c.hrs));
  const maxVal = Math.max(...allValues, 1);

  const clsColor = cls => cls === 'VA' ? '#10b981' : cls === 'BVA' ? '#3b82f6' : cls === 'NVA' ? '#ef4444' : '#64748b';

  function cellBg(val, max, cls) {
    if (view !== 'heatmap' || val === 0) return 'transparent';
    const intensity = Math.min(val / max, 1);
    const base = cls === 'VA' ? [16,185,129] : cls === 'BVA' ? [59,130,246] : cls === 'NVA' ? [239,68,68] : [100,116,139];
    return `rgba(${base[0]},${base[1]},${base[2]},${0.15 + intensity * 0.65})`;
  }

  const thStyle = (step) => ({
    padding:'6px 8px', fontSize:8, fontWeight:700, color:'#64748b',
    fontFamily:"'IBM Plex Mono',monospace", textTransform:'uppercase',
    letterSpacing:'0.08em', textAlign:'right', whiteSpace:'nowrap',
    background:'#0d1e35', position:'sticky', top:0, cursor:'pointer',
    borderBottom:'2px solid #1e293b', minWidth:70,
    borderTop: sortBy===step ? '2px solid #3b82f6' : '2px solid transparent',
    color: sortBy===step ? '#3b82f6' : '#64748b',
  });

  return (
    <div>
      {/* Controls */}
      <div style={{display:'flex',gap:12,alignItems:'center',marginBottom:14,flexWrap:'wrap'}}>
        <div style={{display:'flex',gap:4}}>
          {viewOptions.map(({v,l}) => (
            <button key={v} onClick={()=>setView(v)} style={{
              padding:'4px 12px', fontSize:10, fontWeight:600, borderRadius:5,
              border:'1px solid', cursor:'pointer', fontFamily:"'IBM Plex Mono',monospace",
              background: view===v ? '#0d2244' : 'transparent',
              color: view===v ? '#93c5fd' : '#334155',
              borderColor: view===v ? '#1e3a5f' : '#1e293b',
            }}>{l}</button>
          ))}
        </div>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <span style={{fontSize:9,color:'#475569',fontFamily:"'IBM Plex Mono',monospace"}}>MIN HOURS</span>
          <input type="number" min="0" step="0.5" value={minHrs}
            onChange={e=>setMinHrs(Math.max(0,parseFloat(e.target.value)||0))}
            style={{width:60,background:'#0d2244',border:'1px solid #1e3a5f',borderRadius:4,color:'#93c5fd',fontSize:11,padding:'3px 7px',fontFamily:"'IBM Plex Mono',monospace",textAlign:'center'}}/>
        </div>
        <div style={{marginLeft:'auto',display:'flex',gap:12}}>
          {[['VA','#10b981'],['BVA','#3b82f6'],['NVA','#ef4444']].map(([l,c])=>(
            <span key={l} style={{display:'flex',alignItems:'center',gap:4,fontSize:9,color:'#475569',fontFamily:"'IBM Plex Mono',monospace"}}>
              <span style={{width:8,height:8,borderRadius:'50%',background:c,display:'inline-block'}}/>
              {l}
            </span>
          ))}
        </div>
      </div>

      {/* Summary stats */}
      <div style={{display:'flex',gap:8,marginBottom:14}}>
        {[
          [rows.length, 'Resources', 'unique', '#3b82f6'],
          [steps.length, 'Activities', 'unique', '#8b5cf6'],
          [rows.reduce((s,r)=>s+r.uniqueSteps,0), 'Assignments', 'total', '#10b981'],
          [+(rows.reduce((s,r)=>s+r.totalHrs,0)/rows.length).toFixed(1)+'h', 'Avg Hours', 'per resource', '#f59e0b'],
        ].map(([v,l,u,c])=>(
          <div key={l} style={{background:'#0b1628',border:`1px solid ${c}25`,borderTop:`2px solid ${c}`,borderRadius:6,padding:'7px 12px',minWidth:80}}>
            <div style={{fontSize:8,color:'#475569',fontFamily:"'IBM Plex Mono',monospace",textTransform:'uppercase',letterSpacing:'0.1em',marginBottom:2}}>{l}</div>
            <div style={{fontSize:18,fontWeight:700,color:c,fontFamily:"'IBM Plex Mono',monospace"}}>{v}</div>
            <div style={{fontSize:7.5,color:'#334155',fontFamily:"'IBM Plex Mono',monospace"}}>{u}</div>
          </div>
        ))}
      </div>

      {/* Matrix table */}
      <div style={{overflowX:'auto',overflowY:'auto',maxHeight:500,borderRadius:6,border:'1px solid #1e293b'}}>
        <table style={{borderCollapse:'collapse',fontSize:10,minWidth:'100%'}}>
          <thead>
            <tr>
              {/* Resource header */}
              <th onClick={()=>setSortBy('name')} style={{
                padding:'6px 10px', fontSize:8, fontWeight:700, textAlign:'left',
                background:'#0d1e35', position:'sticky', left:0, top:0, zIndex:3,
                fontFamily:"'IBM Plex Mono',monospace", textTransform:'uppercase',
                letterSpacing:'0.08em', cursor:'pointer', minWidth:140,
                borderRight:'2px solid #1e293b', borderBottom:'2px solid #1e293b',
                color: sortBy==='name' ? '#3b82f6' : '#64748b',
                borderTop: sortBy==='name' ? '2px solid #3b82f6' : '2px solid transparent',
              }}>Resource {sortBy==='name'?'↑':''}</th>
              {/* Total header */}
              <th onClick={()=>setSortBy('total')} style={{
                ...thStyle('total'), textAlign:'right', borderRight:'2px solid #1e293b',
                color: sortBy==='total'?'#f59e0b':'#64748b',
                borderTop: sortBy==='total'?'2px solid #f59e0b':'2px solid transparent',
              }}>TOTAL {sortBy==='total'?'↓':''}</th>
              {/* Step headers */}
              {steps.map(step => {
                const ct = colTotals.find(c=>c.step===step);
                const cls = rows.find(r=>r.cells.find(c=>c.step===step&&c.count>0))?.cells.find(c=>c.step===step)?.cls || '';
                return (
                  <th key={step} onClick={()=>setSortBy(step)} style={{
                    ...thStyle(step),
                    borderTop: sortBy===step?`2px solid ${clsColor(cls)}`:'2px solid transparent',
                    color: sortBy===step ? clsColor(cls) : '#64748b',
                  }}>
                    <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:2}}>
                      <span style={{display:'inline-block',width:6,height:6,borderRadius:'50%',background:clsColor(cls),marginBottom:2}}/>
                      <span style={{maxWidth:80,overflow:'hidden',textOverflow:'ellipsis',display:'block'}}>{step}</span>
                      {ct && ct.hrs > 0 && <span style={{color:'#334155',fontSize:7}}>{ct.hrs}h</span>}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sorted.map((row, ri) => (
              <tr key={row.res} style={{background:ri%2===0?'#070f1c':'#0a1525'}}>
                {/* Resource name */}
                <td style={{
                  padding:'5px 10px', color:'#cbd5e1', fontWeight:600, fontSize:10,
                  position:'sticky', left:0, background:ri%2===0?'#070f1c':'#0a1525',
                  borderRight:'2px solid #1e293b', whiteSpace:'nowrap', zIndex:1,
                }}>
                  <div>{row.res}</div>
                  <div style={{fontSize:8,color:'#334155',fontFamily:"'IBM Plex Mono',monospace",marginTop:2}}>
                    {row.uniqueSteps} activities · {row.totalHrs}h
                  </div>
                </td>
                {/* Total */}
                <td style={{padding:'5px 8px',textAlign:'right',fontFamily:"'IBM Plex Mono',monospace",fontWeight:700,color:'#f59e0b',borderRight:'2px solid #1e293b',fontSize:10}}>
                  {view==='count' ? row.totalCount : row.totalHrs+'h'}
                </td>
                {/* Step cells */}
                {row.cells.map(cell => {
                  const val = view==='count' ? cell.count : cell.hrs;
                  const bg = cellBg(view==='count'?cell.count:cell.hrs, view==='count'?Math.max(...rows.flatMap(r=>r.cells.map(c=>c.count)),1):maxVal, cell.cls);
                  return (
                    <td key={cell.step} style={{
                      padding:'5px 8px', textAlign:'right',
                      fontFamily:"'IBM Plex Mono',monospace", fontSize:9,
                      background: bg,
                      color: val===0 ? '#1e293b' : cell.cls==='VA'?'#10b981':cell.cls==='BVA'?'#3b82f6':cell.cls==='NVA'?'#ef4444':'#94a3b8',
                      fontWeight: val>0 ? 600 : 400,
                    }}>
                      {val===0 ? '—' : view==='count' ? val : val+'h'}
                    </td>
                  );
                })}
              </tr>
            ))}
            {/* Column totals row */}
            <tr style={{background:'#0d1e35',borderTop:'2px solid #1e293b'}}>
              <td style={{padding:'5px 10px',color:'#64748b',fontWeight:700,fontSize:9,position:'sticky',left:0,background:'#0d1e35',borderRight:'2px solid #1e293b',fontFamily:"'IBM Plex Mono',monospace",textTransform:'uppercase',letterSpacing:'0.08em'}}>TOTAL</td>
              <td style={{padding:'5px 8px',textAlign:'right',fontFamily:"'IBM Plex Mono',monospace",fontWeight:700,color:'#f59e0b',borderRight:'2px solid #1e293b',fontSize:10}}>
                {view==='count'
                  ? rows.reduce((s,r)=>s+r.totalCount,0)
                  : +(rows.reduce((s,r)=>s+r.totalHrs,0)).toFixed(2)+'h'}
              </td>
              {colTotals.map(ct => (
                <td key={ct.step} style={{padding:'5px 8px',textAlign:'right',fontFamily:"'IBM Plex Mono',monospace",fontWeight:700,color:'#64748b',fontSize:9}}>
                  {view==='count' ? ct.count : ct.hrs+'h'}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
      <div style={{marginTop:8,fontSize:8.5,color:'#334155',fontFamily:"'IBM Plex Mono',monospace"}}>
        Click any column header to sort by that activity. Min Hours filter hides resources below threshold. Heat Map view shows intensity of activity per resource.
      </div>
    </div>
  );
}

// ── Process Map Panel ─────────────────────────────────────────────────────────
function ProcessMapPanel({ data }) {
  const { nodes, edges, totalCases } = data;
  const [minPct, setMinPct] = React.useState(10);
  const [layout, setLayout] = React.useState('table'); // 'table' | 'flow'

  const filteredEdges = edges.filter(e => e.pct >= minPct);
  const filteredNodes = [...new Set(filteredEdges.flatMap(e => [e.from, e.to]))];
  const maxCount = edges[0]?.count || 1;
  const vaColor = v => v === 'VA' ? '#10b981' : v === 'BVA' ? '#3b82f6' : v === 'NVA' ? '#ef4444' : '#64748b';

  return (
    <div>
      {/* Controls */}
      <div style={{display:'flex',gap:16,alignItems:'center',marginBottom:14,flexWrap:'wrap'}}>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          <span style={{fontSize:10,color:'#475569',fontFamily:"'IBM Plex Mono',monospace"}}>MIN FREQUENCY</span>
          <input type="range" min="0" max="50" value={minPct} onChange={e=>setMinPct(+e.target.value)} style={{width:100,accentColor:'#3b82f6'}}/>
          <span style={{fontSize:10,color:'#3b82f6',fontFamily:"'IBM Plex Mono',monospace",minWidth:32}}>{minPct}%</span>
        </div>
        <div style={{display:'flex',gap:4}}>
          {[['table','📋 Table'],['flow','⬇ Flow']].map(([v,l])=>(
            <button key={v} onClick={()=>setLayout(v)} style={{padding:'4px 10px',fontSize:10,fontWeight:600,borderRadius:5,border:'1px solid',cursor:'pointer',fontFamily:"'IBM Plex Mono',monospace",background:layout===v?'#0d2244':'transparent',color:layout===v?'#93c5fd':'#334155',borderColor:layout===v?'#1e3a5f':'#1e293b'}}>{l}</button>
          ))}
        </div>
        <div style={{display:'flex',gap:12,marginLeft:'auto'}}>
          {[['VA','#10b981'],['BVA','#3b82f6'],['NVA','#ef4444'],['?','#64748b']].map(([l,c])=>(
            <span key={l} style={{display:'flex',alignItems:'center',gap:4,fontSize:9,color:'#475569',fontFamily:"'IBM Plex Mono',monospace"}}>
              <span style={{width:8,height:8,borderRadius:'50%',background:c,display:'inline-block'}}/>
              {l}
            </span>
          ))}
        </div>
      </div>

      {/* Stats row */}
      <div style={{display:'flex',gap:8,marginBottom:14}}>
        {[[nodes.length,'Steps','total unique','#3b82f6'],[edges.length,'Transitions','total paths','#8b5cf6'],[filteredEdges.length,'Shown',`≥${minPct}% freq`,'#10b981'],[totalCases,'Cases','in dataset','#f59e0b']].map(([v,l,u,c])=>(
          <div key={l} style={{background:'#0b1628',border:`1px solid ${c}25`,borderTop:`2px solid ${c}`,borderRadius:6,padding:'7px 12px',minWidth:80}}>
            <div style={{fontSize:8,color:'#475569',fontFamily:"'IBM Plex Mono',monospace",textTransform:'uppercase',letterSpacing:'0.1em',marginBottom:2}}>{l}</div>
            <div style={{fontSize:18,fontWeight:700,color:c,fontFamily:"'IBM Plex Mono',monospace"}}>{v}</div>
            <div style={{fontSize:7.5,color:'#334155',fontFamily:"'IBM Plex Mono',monospace"}}>{u}</div>
          </div>
        ))}
      </div>

      {layout === 'table' ? (
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
          {/* Nodes table */}
          <div>
            <div style={{fontSize:9,fontWeight:700,letterSpacing:'0.14em',textTransform:'uppercase',color:'#334155',fontFamily:"'IBM Plex Mono',monospace",marginBottom:8,paddingLeft:8,borderLeft:'2px solid #1e3a5f'}}>Activity Frequency</div>
            <div style={{overflowY:'auto',maxHeight:380,borderRadius:6,border:'1px solid #1e293b'}}>
              <table style={{borderCollapse:'collapse',width:'100%',fontSize:11}}>
                <thead><tr>
                  <th style={{padding:'6px 10px',background:'#0d1e35',color:'#64748b',textAlign:'left',position:'sticky',top:0,fontSize:9,fontWeight:600,letterSpacing:'0.08em',textTransform:'uppercase'}}>Step</th>
                  <th style={{padding:'6px 10px',background:'#0d1e35',color:'#64748b',textAlign:'center',position:'sticky',top:0,fontSize:9,fontWeight:600,letterSpacing:'0.08em',textTransform:'uppercase'}}>VA</th>
                  <th style={{padding:'6px 10px',background:'#0d1e35',color:'#64748b',textAlign:'right',position:'sticky',top:0,fontSize:9,fontWeight:600,letterSpacing:'0.08em',textTransform:'uppercase'}}>Count</th>
                  <th style={{padding:'6px 10px',background:'#0d1e35',color:'#64748b',textAlign:'right',position:'sticky',top:0,fontSize:9,fontWeight:600,letterSpacing:'0.08em',textTransform:'uppercase'}}>%</th>
                  <th style={{padding:'6px 10px',background:'#0d1e35',color:'#64748b',textAlign:'left',position:'sticky',top:0,fontSize:9,fontWeight:600,letterSpacing:'0.08em',textTransform:'uppercase',width:100}}>Bar</th>
                </tr></thead>
                <tbody>
                  {nodes.map((n, i) => (
                    <tr key={n.step} style={{background:i%2===0?'#070f1c':'#0a1525'}}>
                      <td style={{padding:'5px 10px',color:'#cbd5e1',whiteSpace:'nowrap',overflow:'hidden',maxWidth:180,textOverflow:'ellipsis'}}>{n.step}</td>
                      <td style={{padding:'5px 10px',textAlign:'center'}}>
                        <span style={{display:'inline-block',width:8,height:8,borderRadius:'50%',background:vaColor(n.va)}}/>
                      </td>
                      <td style={{padding:'5px 10px',textAlign:'right',color:'#94a3b8',fontFamily:"'IBM Plex Mono',monospace"}}>{n.count}</td>
                      <td style={{padding:'5px 10px',textAlign:'right',color:'#3b82f6',fontFamily:"'IBM Plex Mono',monospace",fontWeight:600}}>{n.pct}%</td>
                      <td style={{padding:'5px 10px'}}>
                        <div style={{height:6,background:'#1e293b',borderRadius:3,overflow:'hidden'}}>
                          <div style={{height:6,width:`${n.pct}%`,background:vaColor(n.va),borderRadius:3,transition:'width .3s'}}/>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Edges table */}
          <div>
            <div style={{fontSize:9,fontWeight:700,letterSpacing:'0.14em',textTransform:'uppercase',color:'#334155',fontFamily:"'IBM Plex Mono',monospace",marginBottom:8,paddingLeft:8,borderLeft:'2px solid #8b5cf6'}}>Top Transitions</div>
            <div style={{overflowY:'auto',maxHeight:380,borderRadius:6,border:'1px solid #1e293b'}}>
              <table style={{borderCollapse:'collapse',width:'100%',fontSize:11}}>
                <thead><tr>
                  <th style={{padding:'6px 10px',background:'#0d1e35',color:'#64748b',textAlign:'left',position:'sticky',top:0,fontSize:9,fontWeight:600,letterSpacing:'0.08em',textTransform:'uppercase'}}>From → To</th>
                  <th style={{padding:'6px 10px',background:'#0d1e35',color:'#64748b',textAlign:'right',position:'sticky',top:0,fontSize:9,fontWeight:600,letterSpacing:'0.08em',textTransform:'uppercase'}}>Count</th>
                  <th style={{padding:'6px 10px',background:'#0d1e35',color:'#64748b',textAlign:'right',position:'sticky',top:0,fontSize:9,fontWeight:600,letterSpacing:'0.08em',textTransform:'uppercase'}}>%</th>
                </tr></thead>
                <tbody>
                  {filteredEdges.slice(0,50).map((e, i) => (
                    <tr key={`${e.from}-${e.to}`} style={{background:i%2===0?'#070f1c':'#0a1525'}}>
                      <td style={{padding:'5px 10px',color:'#94a3b8',fontSize:10}}>
                        <span style={{color:'#cbd5e1'}}>{e.from.length>18?e.from.slice(0,17)+'…':e.from}</span>
                        <span style={{color:'#3b82f6',margin:'0 4px'}}>→</span>
                        <span style={{color:'#cbd5e1'}}>{e.to.length>18?e.to.slice(0,17)+'…':e.to}</span>
                      </td>
                      <td style={{padding:'5px 10px',textAlign:'right',color:'#94a3b8',fontFamily:"'IBM Plex Mono',monospace"}}>{e.count}</td>
                      <td style={{padding:'5px 10px',textAlign:'right',fontFamily:"'IBM Plex Mono',monospace",fontWeight:600,color:e.pct>=50?'#10b981':e.pct>=25?'#f59e0b':'#64748b'}}>{e.pct}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        /* Flow view - vertical linear flow of top path */
        <div style={{maxWidth:600,margin:'0 auto'}}>
          <div style={{fontSize:9,color:'#475569',fontFamily:"'IBM Plex Mono',monospace",marginBottom:12,textAlign:'center'}}>Showing transitions with ≥{minPct}% frequency · Circle size = relative frequency</div>
          {filteredEdges.slice(0,20).map((e, i) => {
            const w = Math.max(8, Math.round(e.count/maxCount*40));
            const fromNode = nodes.find(n=>n.step===e.from);
            const toNode   = nodes.find(n=>n.step===e.to);
            return (
              <div key={i} style={{display:'flex',flexDirection:'column',alignItems:'center',marginBottom:2}}>
                <div style={{display:'flex',alignItems:'center',gap:10,width:'100%',padding:'6px 12px',background:'#0a1525',borderRadius:8,border:'1px solid #1e293b'}}>
                  <div style={{width:10,height:10,borderRadius:'50%',background:vaColor(fromNode?.va||''),flexShrink:0}}/>
                  <span style={{fontSize:11,color:'#cbd5e1',flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{e.from}</span>
                  <div style={{display:'flex',alignItems:'center',gap:6}}>
                    <div style={{height:3,width:`${Math.max(20,w*2)}px`,background:'#1e3a5f',borderRadius:2,position:'relative',overflow:'hidden'}}>
                      <div style={{height:3,width:`${e.pct*2}%`,background:'#3b82f6',borderRadius:2}}/>
                    </div>
                    <span style={{fontSize:9,color:'#3b82f6',fontFamily:"'IBM Plex Mono',monospace",minWidth:36,textAlign:'right'}}>{e.pct}%</span>
                  </div>
                  <span style={{fontSize:14,color:'#1e3a5f'}}>→</span>
                  <div style={{width:10,height:10,borderRadius:'50%',background:vaColor(toNode?.va||''),flexShrink:0}}/>
                  <span style={{fontSize:11,color:'#cbd5e1',flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{e.to}</span>
                  <span style={{fontSize:9,color:'#334155',fontFamily:"'IBM Plex Mono',monospace"}}>{e.count}x</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Document builders (same logic as standalone app) ─────────────────────────
async function buildWordDoc(content, summary) {
  const { Document, Packer, Paragraph, TextRun, HeadingLevel, BorderStyle, Table, TableRow, TableCell, WidthType, ShadingType } = await import('docx');
  const NAVY='1B2A4A', BLUE='1B4F8A', WHITE='FFFFFF', LGREY='F4F6F8', ICE='D6E8F7';
  const p  = t => new Paragraph({ children:[new TextRun({text:t,font:'Calibri',size:22})], spacing:{before:60,after:120,line:300} });
  const h1 = t => new Paragraph({ heading:HeadingLevel.HEADING_1, children:[new TextRun({text:t,font:'Calibri',size:32,bold:true,color:NAVY})], spacing:{before:480,after:200}, border:{bottom:{style:BorderStyle.SINGLE,size:6,color:BLUE,space:6}} });
  const h2 = t => new Paragraph({ heading:HeadingLevel.HEADING_2, children:[new TextRun({text:t,font:'Calibri',size:26,bold:true,color:BLUE})], spacing:{before:280,after:120} });
  const bul= t => new Paragraph({ bullet:{level:0}, children:[new TextRun({text:t,font:'Calibri',size:22})], spacing:{before:40,after:60} });
  const sp = () => new Paragraph({ children:[new TextRun('')], spacing:{before:80,after:80} });
  const pb = () => new Paragraph({ pageBreakBefore:true, children:[new TextRun('')] });
  const noBdr = () => ({top:{style:BorderStyle.NONE,size:0,color:WHITE},bottom:{style:BorderStyle.NONE,size:0,color:WHITE},left:{style:BorderStyle.NONE,size:0,color:WHITE},right:{style:BorderStyle.NONE,size:0,color:WHITE}});
  const dbdr  = () => ({top:{style:BorderStyle.SINGLE,size:1,color:'D0D7E2'},bottom:{style:BorderStyle.SINGLE,size:1,color:'D0D7E2'},left:{style:BorderStyle.SINGLE,size:1,color:'D0D7E2'},right:{style:BorderStyle.SINGLE,size:1,color:'D0D7E2'}});
  const tc = (text, bg=WHITE, bold=false, color='000000', w=null, sz=20) => new TableCell({ width:w?{size:w,type:WidthType.DXA}:undefined, shading:{fill:bg,type:ShadingType.CLEAR}, borders:dbdr(), margins:{top:80,bottom:80,left:120,right:120}, children:[new Paragraph({children:[new TextRun({text:String(text||''),font:'Calibri',size:sz,bold,color})]})] });

  const sevBg = s => s==='critical'?'FDECEA':s==='positive'?'D8F0E4':'FEF3DC';
  const sevC  = s => s==='critical'?'BF2D2D':s==='positive'?'1A6B3C':'C47A1E';

  const kpiRow = items => new Table({ width:{size:9360,type:WidthType.DXA}, columnWidths:Array(items.length).fill(Math.floor(9360/items.length)),
    rows:[new TableRow({ children:items.map(item=>new TableCell({ shading:{fill:item.bg||ICE,type:ShadingType.CLEAR}, borders:{top:{style:BorderStyle.SINGLE,size:4,color:item.color||NAVY},...dbdr()}, margins:{top:140,bottom:140,left:160,right:160}, children:[
      new Paragraph({spacing:{before:0,after:30},children:[new TextRun({text:item.label.toUpperCase(),font:'Calibri',size:15,color:'6B7A8D'})]}),
      new Paragraph({spacing:{before:0,after:20},children:[new TextRun({text:String(item.value),font:'Calibri',size:36,bold:true,color:item.color||NAVY})]}),
      new Paragraph({spacing:{before:0,after:0},children:[new TextRun({text:item.unit||'',font:'Calibri',size:15,color:'6B7A8D',italics:true})]}),
    ] })) })] });

  const meanHrs = (summary.cycleTime.mean/60).toFixed(1);
  const p95Hrs  = (summary.cycleTime.p95/60).toFixed(1);

  const children = [
    new Paragraph({spacing:{before:1200,after:80},children:[new TextRun({text:'FLOWLEVERAGE CONSULTING',font:'Calibri',size:20,bold:true,color:NAVY,allCaps:true})]}),
    new Paragraph({spacing:{before:0,after:60},children:[new TextRun({text:'AI Diagnostics for Your Operations',font:'Calibri',size:18,color:BLUE,italics:true})]}),
    sp(), sp(),
    new Paragraph({spacing:{before:200,after:80},children:[new TextRun({text:content.title||'Process Analytics Report',font:'Calibri',size:64,bold:true,color:NAVY})]}),
    new Paragraph({spacing:{before:0,after:80},children:[new TextRun({text:(content.client||'')+(content.period?' · '+content.period:''),font:'Calibri',size:28,color:BLUE})]}),
    new Paragraph({spacing:{before:0,after:0},border:{bottom:{style:BorderStyle.SINGLE,size:8,color:BLUE,space:6}},children:[new TextRun('')]}), sp(),
    new Table({ width:{size:9360,type:WidthType.DXA}, columnWidths:[9360], rows:[new TableRow({children:[new TableCell({shading:{fill:ICE,type:ShadingType.CLEAR},borders:{left:{style:BorderStyle.SINGLE,size:8,color:BLUE},...dbdr()},margins:{top:200,bottom:200,left:280,right:280},children:[
      new Paragraph({spacing:{before:0,after:60},children:[new TextRun({text:'KEY FINDING',font:'Calibri',size:18,bold:true,color:BLUE,allCaps:true})]}),
      new Paragraph({spacing:{before:0,after:0},children:[new TextRun({text:content.keyFinding||'',font:'Calibri',size:22,color:NAVY})]})
    ]})]})]}),
    sp(), pb(),
    h1('1.  Executive Summary'), p(content.executiveSummary||''),
    sp(), h2('Key Performance Indicators'),
    kpiRow([{label:'Cases',value:summary.nCases,unit:'episodes',color:NAVY,bg:ICE},{label:'Mean Cycle',value:meanHrs+' hrs',unit:summary.cycleTime.mean+' min',color:'C47A1E',bg:'FEF3DC'},{label:'PCE',value:summary.pce.mean+'%',unit:'value-added',color:'1A6B3C',bg:'D8F0E4'},{label:'NVA',value:summary.vaNva.nva+'%',unit:'waste',color:'BF2D2D',bg:'FDECEA'}]),
    sp(), h2('Key Findings'),
    ...(content.findings||[]).flatMap(f=>[
      new Table({ width:{size:9360,type:WidthType.DXA}, columnWidths:[480,8880], rows:[new TableRow({children:[
        new TableCell({width:{size:480,type:WidthType.DXA},shading:{fill:sevBg(f.severity),type:ShadingType.CLEAR},borders:{top:{style:BorderStyle.SINGLE,size:1,color:sevC(f.severity)},bottom:{style:BorderStyle.SINGLE,size:1,color:sevC(f.severity)},left:{style:BorderStyle.SINGLE,size:6,color:sevC(f.severity)},right:{style:BorderStyle.NONE,size:0,color:WHITE}},margins:{top:120,bottom:120,left:120,right:80},children:[new Paragraph({alignment:'center',children:[new TextRun({text:f.severity==='critical'?'⚠':f.severity==='positive'?'✓':'⚡',font:'Segoe UI Emoji',size:28,color:sevC(f.severity)})]})]  }),
        new TableCell({width:{size:8880,type:WidthType.DXA},shading:{fill:sevBg(f.severity),type:ShadingType.CLEAR},borders:{top:{style:BorderStyle.SINGLE,size:1,color:sevC(f.severity)},bottom:{style:BorderStyle.SINGLE,size:1,color:sevC(f.severity)},left:{style:BorderStyle.NONE,size:0,color:WHITE},right:{style:BorderStyle.SINGLE,size:1,color:sevC(f.severity)}},margins:{top:120,bottom:120,left:160,right:160},children:[
          new Paragraph({spacing:{before:0,after:60},children:[new TextRun({text:f.title,font:'Calibri',size:22,bold:true,color:sevC(f.severity)})]}),
          new Paragraph({spacing:{before:0,after:0},children:[new TextRun({text:f.detail,font:'Calibri',size:20})]})
        ]})
      ]})]  }),
      sp(),
    ]),
    pb(),
    h1('2.  Cycle Time'), p(content.cycleTimeAnalysis||''), sp(),
    kpiRow([{label:'Mean',value:meanHrs+' hrs',unit:summary.cycleTime.mean+' min',color:NAVY,bg:ICE},{label:'Median',value:(summary.cycleTime.median/60).toFixed(1)+' hrs',unit:summary.cycleTime.median+' min',color:NAVY,bg:ICE},{label:'P95',value:p95Hrs+' hrs',unit:summary.cycleTime.p95+' min',color:'BF2D2D',bg:'FDECEA'},{label:'Min',value:(summary.cycleTime.min/60).toFixed(1)+' hrs',unit:'fastest',color:'1A6B3C',bg:'D8F0E4'}]),
    sp(), pb(),
    h1('3.  Process Cycle Efficiency'), p(content.pceAnalysis||''), sp(),
    kpiRow([{label:'Mean PCE',value:summary.pce.mean+'%',unit:'adds value',color:'1A6B3C',bg:'D8F0E4'},{label:'VA Time',value:summary.vaNva.va+'%',unit:'value-added',color:'1A6B3C',bg:'D8F0E4'},{label:'BVA Time',value:summary.vaNva.bva+'%',unit:'business req.',color:'2E7FD4',bg:ICE},{label:'NVA Time',value:summary.vaNva.nva+'%',unit:'waste/rework',color:'BF2D2D',bg:'FDECEA'}]),
    sp(), pb(),
    h1('4.  Step Duration Analysis'), p(content.stepDurationAnalysis||''), sp(),
    ...summary.topSteps.map(s=>bul(`${s.step}: avg ${s.mean} min (n=${s.n}, P95=${s.p95} min)`)),
    sp(), pb(),
    h1('5.  Handoff Analysis'), p(content.handoffAnalysis||''), sp(),
    h1('6.  Resource Utilisation'), p(content.resourceAnalysis||''), sp(),
    ...(summary.topRes||[]).map(r=>bul(`${r.res}: ${r.busyHrs} busy hours, ${r.util}% utilisation, ${r.cases} cases`)),
    sp(), pb(),
    h1('7.  Recommendations'), p(content.conclusion||''), sp(),
    ...(content.recommendations||[]).flatMap((rec,i)=>[
      new Table({ width:{size:9360,type:WidthType.DXA}, columnWidths:[560,8800], rows:[
        new TableRow({children:[
          new TableCell({width:{size:560,type:WidthType.DXA},shading:{fill:NAVY,type:ShadingType.CLEAR},borders:{top:dbdr().top,bottom:{style:BorderStyle.NONE,size:0,color:WHITE},left:dbdr().left,right:{style:BorderStyle.NONE,size:0,color:WHITE}},margins:{top:120,bottom:100,left:100,right:100},children:[new Paragraph({alignment:'center',children:[new TextRun({text:String(rec.number||i+1),font:'Calibri',size:36,bold:true,color:WHITE})]})] }),
          new TableCell({width:{size:8800,type:WidthType.DXA},shading:{fill:ICE,type:ShadingType.CLEAR},borders:{top:dbdr().top,bottom:{style:BorderStyle.NONE,size:0,color:WHITE},left:{style:BorderStyle.NONE,size:0,color:WHITE},right:dbdr().right},margins:{top:100,bottom:80,left:200,right:160},children:[new Paragraph({spacing:{before:0,after:0},children:[new TextRun({text:rec.title||'',font:'Calibri',size:22,bold:true,color:NAVY})]})] }),
        ]}),
        new TableRow({children:[
          new TableCell({width:{size:560,type:WidthType.DXA},shading:{fill:LGREY,type:ShadingType.CLEAR},borders:{top:{style:BorderStyle.NONE},bottom:dbdr().bottom,left:dbdr().left,right:{style:BorderStyle.NONE}},children:[new Paragraph({children:[new TextRun('')]})] }),
          new TableCell({width:{size:8800,type:WidthType.DXA},shading:{fill:WHITE,type:ShadingType.CLEAR},borders:{top:{style:BorderStyle.NONE},bottom:dbdr().bottom,left:{style:BorderStyle.NONE},right:dbdr().right},margins:{top:80,bottom:120,left:200,right:160},children:[
            new Paragraph({spacing:{before:0,after:80},children:[new TextRun({text:rec.description||'',font:'Calibri',size:20})]}),
            new Paragraph({spacing:{before:0,after:0},children:[
              new TextRun({text:'Impact: ',font:'Calibri',size:20,bold:true,color:NAVY}), new TextRun({text:(rec.impact||'')+'   ',font:'Calibri',size:20}),
              new TextRun({text:'Effort: ',font:'Calibri',size:20,bold:true,color:NAVY}), new TextRun({text:(rec.effort||'')+'   ',font:'Calibri',size:20,color:rec.effort==='Low'?'1A6B3C':rec.effort==='Medium'?'C47A1E':'BF2D2D',bold:true}),
              new TextRun({text:'Value: ',font:'Calibri',size:20,bold:true,color:NAVY}), new TextRun({text:rec.saving||'',font:'Calibri',size:20,color:'1A6B3C',bold:true}),
            ]}),
          ] }),
        ]}),
      ] }),
      sp(),
    ]),
  ];

  const doc = new Document({ styles:{default:{document:{run:{font:'Calibri',size:22}}}}, sections:[{ properties:{page:{size:{width:12240,height:15840},margin:{top:1080,right:1080,bottom:1080,left:1260}}}, children }] });
  const buf = await Packer.toBuffer(doc);
  return new Blob([buf], {type:'application/vnd.openxmlformats-officedocument.wordprocessingml.document'});
}

async function buildPPTXDoc(sd, summary) {
  if (!window.PptxGenJS) {
    await new Promise((resolve,reject)=>{ const s=document.createElement('script'); s.src='https://cdn.jsdelivr.net/npm/pptxgenjs@3.12.0/dist/pptxgen.bundle.js'; s.onload=resolve; s.onerror=reject; document.head.appendChild(s); });
  }
  const pres = new window.PptxGenJS();
  pres.layout='LAYOUT_16x9';
  const B={navy:'0D1B3E',blue:'1B4F8A',lblue:'2E7FD4',gold:'D4A017',white:'FFFFFF',lgrey:'F4F6F8',teal:'0A7E7E',amber:'C47A1E',red:'BF2D2D',green:'1A6B3C',mgrey:'A0ABBE',dgrey:'4A5568'};
  const foot=s=>{s.addShape(pres.shapes.RECTANGLE,{x:0,y:5.42,w:10,h:0.205,fill:{color:B.navy},line:{color:B.navy}});s.addShape(pres.shapes.RECTANGLE,{x:0,y:5.42,w:1.6,h:0.205,fill:{color:B.gold},line:{color:B.gold}});s.addText('ProcessMD',{x:0.08,y:5.43,w:1.44,h:0.18,fontSize:7.5,bold:true,color:B.navy,valign:'middle'});s.addText('ProcessMD  |  AI Diagnostics for Your Operations  |  CONFIDENTIAL',{x:1.7,y:5.43,w:8.2,h:0.18,fontSize:7,color:'8BA3C7',valign:'middle'});};
  const hdr=(s,n,t)=>{s.background={color:B.white};s.addShape(pres.shapes.RECTANGLE,{x:0,y:0,w:10,h:1,fill:{color:B.navy},line:{color:B.navy}});s.addShape(pres.shapes.RECTANGLE,{x:0,y:0,w:0.18,h:5.625,fill:{color:B.gold},line:{color:B.gold}});s.addText(`${n}  ${t}`,{x:0.5,y:0.22,w:9,h:0.55,fontSize:20,bold:true,color:B.white});};
  const kpi=(s,x,y,w,h,l,v,u,c=B.lblue)=>{s.addShape(pres.shapes.RECTANGLE,{x,y,w,h,fill:{color:B.white},line:{color:'E2E8F0',pt:0.5}});s.addShape(pres.shapes.RECTANGLE,{x,y,w,h:0.06,fill:{color:c},line:{color:c}});s.addText(l.toUpperCase(),{x:x+0.12,y:y+0.14,w:w-0.24,h:0.22,fontSize:7.5,color:B.mgrey,bold:true,charSpacing:2});s.addText(v,{x:x+0.12,y:y+0.35,w:w-0.24,h:0.65,fontSize:24,bold:true,color:B.navy,valign:'middle'});s.addText(u,{x:x+0.12,y:y+0.98,w:w-0.24,h:0.2,fontSize:8,color:B.mgrey,italic:true});};
  const meanHrs=(summary.cycleTime.mean/60).toFixed(1);
  const p95Hrs=(summary.cycleTime.p95/60).toFixed(1);
  const sevC=s=>s==='critical'?B.red:s==='positive'?B.teal:B.amber;

  // Cover
  {const s=pres.addSlide();s.background={color:'040c18'};s.addShape(pres.shapes.RECTANGLE,{x:0,y:0,w:0.35,h:5.625,fill:{color:B.gold},line:{color:B.gold}});s.addShape(pres.shapes.RECTANGLE,{x:6.8,y:0,w:3.2,h:5.625,fill:{color:'0A1428'},line:{color:'0A1428'}});s.addShape(pres.shapes.RECTANGLE,{x:0.35,y:4.1,w:9.65,h:0.004,fill:{color:B.lblue},line:{color:B.lblue}});s.addText('ProcessMD',{x:0.6,y:0.28,w:5,h:0.3,fontSize:11,bold:true,color:B.gold});s.addText('AI Diagnostics for Your Operations',{x:0.6,y:0.58,w:5.5,h:0.22,fontSize:8.5,color:'8BA3C7',italic:true});s.addText(sd.processName||'Process',{x:0.6,y:1.05,w:8,h:0.85,fontSize:38,bold:true,color:B.white});s.addText('Process Analytics Report',{x:0.6,y:1.88,w:8,h:0.7,fontSize:32,bold:true,color:B.lblue});s.addShape(pres.shapes.RECTANGLE,{x:0.6,y:3.72,w:5.9,h:0.9,fill:{color:'0D1E38'},line:{color:'1B3A6B',pt:0.5}});[[sd.client||'Client','For:'],[sd.period||'Analysis Period','Period:'],['ProcessMD','By:']].forEach(([v,l],i)=>{s.addText(l,{x:0.78,y:3.82+i*0.26,w:0.9,h:0.22,fontSize:8,color:'6B8BB8',bold:true});s.addText(v,{x:1.72,y:3.82+i*0.26,w:4.6,h:0.22,fontSize:8,color:B.white});});[[summary.nCases,'cases'],[meanHrs+' hrs','mean cycle'],[summary.pce.mean+'%','mean PCE'],[summary.vaNva.nva+'%','NVA time']].forEach(([v,u],i)=>{s.addText(v,{x:7.0,y:1.38+i*0.95,w:2.8,h:0.55,fontSize:26,bold:true,color:B.white});s.addText(u,{x:7.0,y:1.88+i*0.95,w:2.8,h:0.2,fontSize:8.5,color:'6B8BB8'});if(i<3)s.addShape(pres.shapes.RECTANGLE,{x:7.0,y:2.24+i*0.95,w:2.5,h:0.002,fill:{color:'1B3A6B'},line:{color:'1B3A6B'}});});foot(s);}
  // Exec summary
  {const s=pres.addSlide();hdr(s,'01','Executive Summary');s.addShape(pres.shapes.RECTANGLE,{x:0.3,y:1.08,w:9.4,h:0.72,fill:{color:'D6E8F7'},line:{color:B.lblue,pt:0.8}});s.addShape(pres.shapes.RECTANGLE,{x:0.3,y:1.08,w:0.08,h:0.72,fill:{color:B.lblue},line:{color:B.lblue}});s.addText('KEY FINDING',{x:0.55,y:1.11,w:1.8,h:0.2,fontSize:7.5,bold:true,color:B.lblue,charSpacing:3});s.addText(sd.keyFinding||'',{x:0.55,y:1.3,w:9.0,h:0.44,fontSize:9.5,color:B.navy});kpi(s,0.3,2.0,2.2,1.2,'Cases',summary.nCases.toString(),'episodes',B.lblue);kpi(s,2.7,2.0,2.2,1.2,'Mean Cycle',meanHrs+' hrs',summary.cycleTime.mean+' min',B.amber);kpi(s,5.1,2.0,2.2,1.2,'PCE',summary.pce.mean+'%','efficiency',B.teal);kpi(s,7.5,2.0,2.2,1.2,'NVA',summary.vaNva.nva+'%','waste',B.red);(sd.execBullets||[]).forEach((b,i)=>{s.addShape(pres.shapes.RECTANGLE,{x:0.3,y:3.38+i*0.6,w:0.07,h:0.44,fill:{color:B.lblue},line:{color:B.lblue}});s.addText(b,{x:0.48,y:3.38+i*0.6,w:9.2,h:0.52,fontSize:9,color:B.dgrey,wrap:true});});foot(s);}
  // Findings
  {const s=pres.addSlide();hdr(s,'02','Key Findings');[[sd.finding1,0],[sd.finding2,1],[sd.finding3,2]].forEach(([f,i])=>{if(!f)return;const c=sevC(f.severity),ic=f.severity==='critical'?'⚠':f.severity==='positive'?'✓':'⚡';s.addShape(pres.shapes.RECTANGLE,{x:0.3,y:1.12+i*1.42,w:9.4,h:1.3,fill:{color:B.lgrey},line:{color:'E2E8F0'}});s.addShape(pres.shapes.RECTANGLE,{x:0.3,y:1.12+i*1.42,w:0.08,h:1.3,fill:{color:c},line:{color:c}});s.addText(ic,{x:0.46,y:1.18+i*1.42,w:0.42,h:0.42,fontSize:18});s.addText(f.title,{x:0.95,y:1.18+i*1.42,w:8.6,h:0.3,fontSize:10.5,bold:true,color:B.navy});s.addText(f.body,{x:0.95,y:1.52+i*1.42,w:8.6,h:0.78,fontSize:9,color:B.dgrey,wrap:true});});foot(s);}
  // Cycle + PCE
  {const s=pres.addSlide();hdr(s,'03','Cycle Time & PCE');kpi(s,0.28,1.1,1.78,1.1,'Mean',meanHrs+' hrs',summary.cycleTime.mean+' min',B.lblue);kpi(s,2.28,1.1,1.78,1.1,'Median',(summary.cycleTime.median/60).toFixed(1)+' hrs',summary.cycleTime.median+' min',B.lblue);kpi(s,4.28,1.1,1.78,1.1,'P95',p95Hrs+' hrs','worst 5%',B.red);kpi(s,6.28,1.1,1.78,1.1,'PCE',summary.pce.mean+'%','efficiency',B.teal);kpi(s,8.28,1.1,1.78,1.1,'NVA',summary.vaNva.nva+'%','waste',B.red);s.addChart(pres.charts.DOUGHNUT,[{name:'Time',labels:['VA','BVA','NVA'],values:[parseFloat(summary.vaNva.va),parseFloat(summary.vaNva.bva),parseFloat(summary.vaNva.nva)]}],{x:0.3,y:2.38,w:3.8,h:2.9,chartColors:['1A6B3C','2E7FD4','BF2D2D'],showPercent:true,showLegend:true,legendPos:'b',legendFontSize:9,showTitle:true,title:'Activity Time Split (%)',titleFontSize:9,titleColor:B.navy,dataLabelFontSize:10,dataLabelColor:B.white,dataLabelFontBold:true,chartArea:{fill:{color:B.white}},holeSize:55,});s.addShape(pres.shapes.RECTANGLE,{x:4.3,y:2.38,w:5.4,h:2.9,fill:{color:B.lgrey},line:{color:'E2E8F0'}});s.addShape(pres.shapes.RECTANGLE,{x:4.3,y:2.38,w:5.4,h:0.34,fill:{color:B.blue},line:{color:B.blue}});s.addText('INTERPRETATION',{x:4.45,y:2.41,w:5.1,h:0.28,fontSize:8.5,bold:true,color:B.white,charSpacing:2});(sd.ctInterpretation||[]).forEach((b,i)=>{s.addShape(pres.shapes.RECTANGLE,{x:4.4,y:2.82+i*0.6,w:0.07,h:0.44,fill:{color:B.lblue},line:{color:B.lblue}});s.addText(b,{x:4.56,y:2.82+i*0.6,w:5.0,h:0.52,fontSize:8.5,color:B.dgrey,wrap:true});});foot(s);}
  // Steps + Resources
  {const s=pres.addSlide();hdr(s,'04','Bottlenecks & Resources');s.addChart(pres.charts.BAR,[{name:'Avg Duration (min)',labels:summary.topSteps.map(t=>t.step.length>18?t.step.slice(0,17)+'…':t.step),values:summary.topSteps.map(t=>parseFloat(t.mean))}],{x:0.3,y:1.1,w:5.8,h:2.8,barDir:'bar',chartColors:summary.topSteps.map(t=>t.step.toLowerCase().includes('wait')||t.step.toLowerCase().includes('re-')?'BF2D2D':'1B4F8A'),chartArea:{fill:{color:B.lgrey}},catAxisLabelColor:B.dgrey,catAxisLabelFontSize:8,valAxisLabelColor:B.dgrey,valGridLine:{color:'E2E8F0'},catGridLine:{style:'none'},showValue:true,dataLabelColor:B.navy,dataLabelFontSize:8,showLegend:false,showTitle:true,title:'Top Steps by Mean Duration (min)',titleFontSize:9,titleColor:B.navy,});s.addShape(pres.shapes.RECTANGLE,{x:6.25,y:1.1,w:3.45,h:2.8,fill:{color:B.lgrey},line:{color:'E2E8F0'}});s.addShape(pres.shapes.RECTANGLE,{x:6.25,y:1.1,w:3.45,h:0.35,fill:{color:B.blue},line:{color:B.blue}});s.addText('TOP RESOURCES',{x:6.38,y:1.13,w:3.1,h:0.28,fontSize:8.5,bold:true,color:B.white,charSpacing:2});summary.topRes.slice(0,5).forEach((r,i)=>{const bw=Math.max(0.2,(r.busyHrs/summary.topRes[0].busyHrs)*2.8);s.addText(r.res.length>22?r.res.slice(0,21)+'…':r.res,{x:6.38,y:1.55+i*0.46,w:3.1,h:0.2,fontSize:8,color:B.navy,bold:true});s.addShape(pres.shapes.RECTANGLE,{x:6.38,y:1.74+i*0.46,w:bw,h:0.18,fill:{color:B.lblue},line:{color:B.lblue}});s.addText(r.busyHrs+'h ('+r.util+'%)',{x:6.38+bw+0.05,y:1.73+i*0.46,w:1.3,h:0.18,fontSize:7.5,color:B.dgrey});});s.addShape(pres.shapes.RECTANGLE,{x:0.3,y:4.02,w:9.4,h:0.28,fill:{color:B.navy},line:{color:B.navy}});s.addText('STEP INSIGHT: '+( sd.stepInsight||''),{x:0.4,y:4.04,w:9.1,h:0.22,fontSize:8.5,color:B.white,wrap:true});foot(s);}
  // Recommendations
  {const s=pres.addSlide();hdr(s,'05','Recommendations');const recs=[sd.rec1,sd.rec2,sd.rec3,sd.rec4].filter(Boolean);recs.forEach((r,i)=>{const y=1.1+i*1.04,ec=r.effort==='Low'?B.teal:r.effort==='Medium'?B.amber:B.red;s.addShape(pres.shapes.RECTANGLE,{x:0.3,y,w:9.4,h:0.94,fill:{color:B.lgrey},line:{color:'E2E8F0'}});s.addShape(pres.shapes.RECTANGLE,{x:0.3,y,w:0.48,h:0.94,fill:{color:B.navy},line:{color:B.navy}});s.addText(String(i+1),{x:0.3,y:y+0.24,w:0.48,h:0.42,fontSize:16,bold:true,color:B.white,align:'center'});s.addText(r.title,{x:0.9,y:y+0.06,w:8.55,h:0.26,fontSize:10,bold:true,color:B.navy});s.addText(r.body,{x:0.9,y:y+0.35,w:6.5,h:0.22,fontSize:8.5,color:B.dgrey});s.addShape(pres.shapes.RECTANGLE,{x:7.5,y:y+0.3,w:0.8,h:0.26,fill:{color:ec},line:{color:ec}});s.addText(r.effort,{x:7.5,y:y+0.3,w:0.8,h:0.26,fontSize:7.5,bold:true,color:B.white,align:'center',valign:'middle'});s.addText(r.saving,{x:8.4,y:y+0.32,w:1.25,h:0.22,fontSize:9,bold:true,color:B.green,align:'right'});});s.addShape(pres.shapes.RECTANGLE,{x:0.3,y:5.3,w:9.4,h:0.12,fill:{color:B.navy},line:{color:B.navy}});s.addText('COMBINED ESTIMATE: '+(sd.totalSaving||'')+'   |   '+( sd.nextStep||''),{x:0.4,y:5.26,w:9.1,h:0.18,fontSize:8.5,bold:true,color:B.navy,align:'center'});foot(s);}
  // Closing
  {const s=pres.addSlide();s.background={color:'040c18'};s.addShape(pres.shapes.RECTANGLE,{x:0,y:0,w:0.35,h:5.625,fill:{color:B.gold},line:{color:B.gold}});s.addShape(pres.shapes.RECTANGLE,{x:0.35,y:2.8,w:9.65,h:0.004,fill:{color:B.lblue},line:{color:B.lblue}});s.addText('ProcessMD',{x:0.6,y:0.28,w:7,h:0.32,fontSize:13,bold:true,color:B.gold});s.addText('AI Diagnostics for Your Operations',{x:0.6,y:0.6,w:6,h:0.22,fontSize:9,color:'8BA3C7',italic:true});s.addText('Thank you',{x:0.6,y:1.05,w:9,h:0.8,fontSize:44,bold:true,color:B.white});s.addText('We look forward to supporting your process improvement programme.',{x:0.6,y:1.9,w:7,h:0.5,fontSize:14,color:'8BA3C7'});foot(s);}

  const blob=await pres.write({outputType:'blob'});
  return blob;
}

const SC = {
  row:{display:'flex',gap:8,flexWrap:'wrap',marginBottom:14},
  slabel:{fontSize:9,fontWeight:700,letterSpacing:'0.14em',textTransform:'uppercase',color:'#334155',fontFamily:"'IBM Plex Mono',monospace",margin:'16px 0 8px',paddingLeft:8,borderLeft:'2px solid #1e3a5f'},
};

const S = {
  page:     {display:'flex',flexDirection:'column',height:'100vh',background:'#040c18',fontFamily:"'DM Sans',system-ui,sans-serif",color:'#e2e8f0',overflow:'hidden'},
  topbar:   {borderBottom:'1px solid #0f1a2e',padding:'10px 18px',display:'flex',alignItems:'center',justifyContent:'space-between',background:'#02060e',flexShrink:0,gap:12},
  backBtn:  {background:'transparent',border:'1px solid #1e2d4a',color:'#475569',borderRadius:6,padding:'5px 12px',fontSize:11.5,cursor:'pointer',fontFamily:"'IBM Plex Mono',monospace"},
  fileInfo: {display:'flex',alignItems:'center',gap:8,fontSize:10.5,fontFamily:"'IBM Plex Mono',monospace",flex:1,overflow:'hidden'},
  xlsBtn:   {background:'#071a0e',color:'#4ade80',border:'1px solid #14532d',padding:'7px 14px',borderRadius:6,fontSize:11,fontWeight:600,cursor:'pointer',fontFamily:"'IBM Plex Mono',monospace"},
  statusBar:{background:'#1a0a2e',borderBottom:'1px solid #5b21b6',padding:'7px 18px',fontSize:11,color:'#c084fc',fontFamily:"'IBM Plex Mono',monospace"},
  sidebar:  {width:172,background:'#03070f',borderRight:'1px solid #0f1a2e',padding:'10px 8px',overflowY:'auto',flexShrink:0},
  grpLabel: {fontSize:8,fontWeight:700,letterSpacing:'0.18em',textTransform:'uppercase',color:'#1e3a5f',padding:'8px 6px 4px',fontFamily:"'IBM Plex Mono',monospace"},
  tabBtn:   {border:'none',cursor:'pointer',padding:'7px 10px',borderRadius:6,fontSize:11.5,fontWeight:500,transition:'all .15s',display:'flex',alignItems:'center',gap:7,width:'100%',textAlign:'left',fontFamily:"'DM Sans',system-ui,sans-serif",marginBottom:2},
  content:  {flex:1,overflowY:'auto',padding:'18px 22px'},
  center:   {display:'flex',alignItems:'center',justifyContent:'center',height:'100%'},
  errorBox: {background:'#150404',border:'1px solid #450a0a',borderRadius:8,padding:14,color:'#fca5a5',fontSize:12,fontFamily:"'IBM Plex Mono',monospace"},
};
